// Phase 1: hardcoded. Future: fetched from a ReviewInviteTemplate table per practice.
// Keep tag value sets in sync with backend/src/modules/reviews/reviewAssistThemes.ts

export type ReviewPrimaryTheme =
  | 'clear_explanations'
  | 'gentle_care'
  | 'kind_team'
  | 'little_or_no_wait'
  | 'easy_scheduling_checkin'
  | 'clean_comfortable_office'
  | 'honest_no_pressure_care'
  | 'something_else';

export type ReviewSupportingDetail =
  | 'answered_questions'
  | 'knew_what_to_expect'
  | 'next_steps_clear'
  | 'explained_options'
  | 'checked_in_with_me'
  | 'took_their_time'
  | 'visit_felt_comfortable'
  | 'they_were_careful'
  | 'felt_listened_to'
  | 'front_desk_helpful'
  | 'team_was_patient'
  | 'visit_felt_personal'
  | 'seen_quickly'
  | 'checkin_easy'
  | 'appointment_moved_efficiently'
  | 'timing_worked_well'
  | 'booking_simple'
  | 'paperwork_easy'
  | 'reminders_helpful'
  | 'front_desk_made_smooth'
  | 'office_felt_clean'
  | 'space_felt_calm'
  | 'room_was_comfortable'
  | 'office_felt_modern'
  | 'did_not_feel_rushed'
  | 'did_not_feel_pressured'
  | 'recommendation_felt_honest'
  | 'understood_choices'
  | 'handled_smoothly'
  | 'felt_comfortable'
  | 'easier_than_expected'
  | 'feel_good_coming_back';

// Generic, PHI-free reasons for the visit. Keep value set in sync with
// backend/src/modules/reviews/reviewAssistThemes.ts (REASON_FOR_VISIT_VALUES).
export type ReviewReasonForVisit =
  | 'routine_checkup'
  | 'specific_treatment'
  | 'first_visit'
  | 'urgent_visit'
  | 'ongoing_care'
  | 'consultation';

// Tag values are plain strings — the server resolves the vertical/specialty
// question set and validates submitted tags against it.
export interface ReviewAssistAnswers {
  rating: number;
  primaryTheme: string;
  supportingDetails: string[];
  reasonForVisit?: string;
  people?: string[];
  optionalDetail?: string;
}

/** One tappable option, as delivered by the server (steps 1–3). */
export interface CaptureQuestionOption {
  value: string;
  label: string;
  sublabel?: string;
}

/** Vertical/specialty-aware question sets served on the invite resolution. */
export interface CaptureQuestionSet {
  themes: CaptureQuestionOption[];
  detailsByTheme: Record<string, CaptureQuestionOption[]>;
  /** Empty when the reason step is disabled — skip that step entirely. */
  reasons: CaptureQuestionOption[];
}

export const MAX_SUPPORTING_DETAILS = 2;
// Real reviews routinely thank several people ("Thank you, Dr. …, …, and …!"),
// so the cap covers chips + typed write-ins combined. Mirrored by the zod
// bound on the backend assist schema — keep the two in sync.
export const MAX_PEOPLE = 5;

/**
 * Keeps the people write-in inputs in "one more appears once you fill one"
 * shape: all filled entries, plus a single trailing empty input while the
 * combined chips + write-ins count leaves room under MAX_PEOPLE.
 */
export function normalizePeopleWriteIns(writeIns: string[], chipCount: number): string[] {
  const filled = writeIns.filter((w) => w.trim() !== '');
  const hasRoom = chipCount + filled.length < MAX_PEOPLE;
  return hasRoom ? [...filled, ''] : filled.slice(0, Math.max(0, MAX_PEOPLE - chipCount));
}

/** Chips + typed write-ins, deduped case-insensitively, capped at MAX_PEOPLE. */
export function combinePeople(chips: string[], writeIns: string[]): string[] {
  const seen = new Set(chips.map((p) => p.trim().toLowerCase()));
  const typed: string[] = [];
  for (const raw of writeIns) {
    const name = raw.replace(/\s+/g, ' ').trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    typed.push(name);
  }
  return [...chips, ...typed].slice(0, MAX_PEOPLE);
}

export const PRIMARY_THEME_PROMPT = 'What stood out most?';
export const SUPPORTING_DETAIL_PROMPT = 'Anything else about your visit?';
export const REASON_PROMPT = 'What brought you in?';
export const PEOPLE_PROMPT = 'Who took great care of you?';

export const OWN_WORDS_PROMPT = 'Anything you want to add in your own words?';
export const OWN_WORDS_SUBTITLE =
  'This is the part people read closest — even a sentence in your own words makes the review sound like you, not a form.';
export const OWN_WORDS_PLACEHOLDER =
  'Example: "The front desk helped me move my appointment last minute" or "I was nervous but they walked me through it."';
export const OWN_WORDS_PRIVACY_NOTE = "Don't include anything you wouldn't want public.";
// Mirrors backend/src/modules/reviews/capture.controller.ts assistSchema.optionalDetail — keep in sync.
export const OWN_WORDS_MAX_LENGTH = 500;

export const REVIEW_ASSIST_REASONS: ReadonlyArray<{
  label: string;
  value: ReviewReasonForVisit;
}> = [
  { label: 'A routine checkup or cleaning', value: 'routine_checkup' },
  { label: 'A specific treatment', value: 'specific_treatment' },
  { label: 'My first visit here', value: 'first_visit' },
  { label: 'An urgent or last-minute visit', value: 'urgent_visit' },
  { label: 'Ongoing or follow-up care', value: 'ongoing_care' },
  { label: 'A consultation', value: 'consultation' },
] as const;

export const REVIEW_ASSIST_PRIMARY_THEMES: ReadonlyArray<{
  label: string;
  value: ReviewPrimaryTheme;
}> = [
  { label: 'Clear explanations', value: 'clear_explanations' },
  { label: 'Gentle care', value: 'gentle_care' },
  { label: 'A kind team', value: 'kind_team' },
  { label: 'Little or no wait', value: 'little_or_no_wait' },
  { label: 'Easy scheduling or check-in', value: 'easy_scheduling_checkin' },
  { label: 'Clean, comfortable office', value: 'clean_comfortable_office' },
  { label: 'Honest, no-pressure care', value: 'honest_no_pressure_care' },
  { label: 'Something else', value: 'something_else' },
] as const;

export const REVIEW_ASSIST_SUPPORTING_DETAILS: Record<
  ReviewPrimaryTheme,
  ReadonlyArray<{ label: string; value: ReviewSupportingDetail }>
> = {
  clear_explanations: [
    { label: 'They answered my questions', value: 'answered_questions' },
    { label: 'I knew what to expect', value: 'knew_what_to_expect' },
    { label: 'The next steps were clear', value: 'next_steps_clear' },
    { label: 'They explained my options', value: 'explained_options' },
  ],
  gentle_care: [
    { label: 'They checked in with me', value: 'checked_in_with_me' },
    { label: 'They took their time', value: 'took_their_time' },
    { label: 'The visit felt comfortable', value: 'visit_felt_comfortable' },
    { label: 'They were careful', value: 'they_were_careful' },
  ],
  kind_team: [
    { label: 'I felt listened to', value: 'felt_listened_to' },
    { label: 'The front desk was helpful', value: 'front_desk_helpful' },
    { label: 'The team was patient', value: 'team_was_patient' },
    { label: 'The visit felt personal', value: 'visit_felt_personal' },
  ],
  little_or_no_wait: [
    { label: 'I was seen quickly', value: 'seen_quickly' },
    { label: 'Check-in was easy', value: 'checkin_easy' },
    { label: 'The appointment moved efficiently', value: 'appointment_moved_efficiently' },
    { label: 'The timing worked well', value: 'timing_worked_well' },
  ],
  easy_scheduling_checkin: [
    { label: 'Booking was simple', value: 'booking_simple' },
    { label: 'Paperwork was easy', value: 'paperwork_easy' },
    { label: 'Reminders were helpful', value: 'reminders_helpful' },
    { label: 'The front desk made it smooth', value: 'front_desk_made_smooth' },
  ],
  clean_comfortable_office: [
    { label: 'The office felt clean', value: 'office_felt_clean' },
    { label: 'The space felt calm', value: 'space_felt_calm' },
    { label: 'The room was comfortable', value: 'room_was_comfortable' },
    { label: 'The office felt modern', value: 'office_felt_modern' },
  ],
  honest_no_pressure_care: [
    { label: 'I didn’t feel rushed', value: 'did_not_feel_rushed' },
    { label: 'I didn’t feel pressured', value: 'did_not_feel_pressured' },
    { label: 'The recommendation felt honest', value: 'recommendation_felt_honest' },
    { label: 'I understood my choices', value: 'understood_choices' },
  ],
  something_else: [
    { label: 'They handled everything smoothly', value: 'handled_smoothly' },
    { label: 'I felt comfortable', value: 'felt_comfortable' },
    { label: 'It was easier than expected', value: 'easier_than_expected' },
    { label: 'I’d feel good coming back', value: 'feel_good_coming_back' },
  ],
} as const;

// Fallback question set for deploy skew — used only if the invite resolution
// from an older backend has no `questions` payload. Mirrors the legacy dental
// set above.
export const LEGACY_QUESTION_SET: CaptureQuestionSet = {
  themes: REVIEW_ASSIST_PRIMARY_THEMES.map((t) => ({ value: t.value, label: t.label })),
  detailsByTheme: Object.fromEntries(
    Object.entries(REVIEW_ASSIST_SUPPORTING_DETAILS).map(([theme, options]) => [
      theme,
      options.map((o) => ({ value: o.value, label: o.label })),
    ]),
  ),
  reasons: REVIEW_ASSIST_REASONS.map((r) => ({ value: r.value, label: r.label })),
};
